import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Crosshair, Car, Shield, AlertTriangle, CheckCircle2, Clock, Sparkles, MessageCircle, Send, Download, CreditCard, FileText } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PIPELINE = [
  { key: "intake", label: "Received" },
  { key: "adas_analysis", label: "ADAS Analysis" },
  { key: "approved", label: "Approved" },
  { key: "in_calibration", label: "Calibration" },
  { key: "completed", label: "Completed" },
  { key: "paid", label: "Paid" },
];

const statusColor = {
  intake: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  adas_analysis: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  approved: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  in_calibration: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  paid: "bg-emerald-600/30 text-emerald-200 border-emerald-500/40",
};

export default function TrackJob() {
  const { jobToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [authorName, setAuthorName] = useState(localStorage.getItem("trueline_track_name") || "");
  const [sending, setSending] = useState(false);

  const loadComments = () => {
    axios.get(`${BACKEND_URL}/api/public/track/${jobToken}/comments`).then((r) => setComments(r.data));
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/track/${jobToken}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invalid tracking link"));
    loadComments();
    const t = setInterval(loadComments, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobToken]);

  const sendComment = async () => {
    const body = newComment.trim();
    if (!body) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/track/${jobToken}/comment`, { body, author_name: authorName });
      if (authorName) localStorage.setItem("trueline_track_name", authorName);
      setNewComment("");
      toast.success("Message sent");
      loadComments();
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="font-display text-2xl font-semibold">Invalid Tracking Link</h1>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-[#030712] flex items-center justify-center text-slate-500">Loading...</div>;
  }

  const { job, adas, commission } = data;
  const invoices = data.invoices || [];
  const calReport = data.calibration_report;
  const currentStepIdx = STATUS_PIPELINE.findIndex((s) => s.key === job.status);

  const payInvoice = async (invoiceId) => {
    try {
      const successUrl = window.location.origin + `/track/${jobToken}?paid=1`;
      const cancelUrl = window.location.origin + `/track/${jobToken}`;
      const r = await axios.post(`${BACKEND_URL}/api/public/track/${jobToken}/pay`, {
        invoice_id: invoiceId, success_url: successUrl, cancel_url: cancelUrl,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success("Payment recorded");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100" data-testid="track-page">
      <Toaster position="top-right" theme="dark" richColors />
      <header className="border-b border-slate-800/80 bg-[#050a17] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center glow-blue">
              <Crosshair className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">TrueLine</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-slate-500 font-bold">Job Tracking</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1120] to-[#050a17] p-8">
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-400 font-bold mb-3">Submission #{job.id.slice(0, 8)}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{job.vehicle}</h1>
          <div className="font-mono text-sm text-blue-300 mt-2">{job.vin}</div>
          <div className="flex items-center gap-3 mt-4">
            <Badge className={`${statusColor[job.status]} border uppercase tracking-wider text-[10px]`} data-testid="job-status-badge">{job.status.replace(/_/g, " ")}</Badge>
            <span className="text-xs text-slate-500">Submitted {new Date(job.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Pipeline progress */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-6">Pipeline</div>
          <div className="relative">
            <div className="absolute top-3 left-3 right-3 h-px bg-slate-800" />
            <div
              className="absolute top-3 left-3 h-px bg-blue-500 transition-all"
              style={{ width: currentStepIdx >= 0 ? `calc(${(currentStepIdx / (STATUS_PIPELINE.length - 1)) * 100}% - 12px)` : "0%" }}
            />
            <div className="grid grid-cols-6 gap-2 relative">
              {STATUS_PIPELINE.map((step, i) => {
                const done = i < currentStepIdx;
                const active = i === currentStepIdx;
                return (
                  <div key={step.key} className="flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center z-10 ${
                      done ? "bg-blue-600 border-blue-500" : active ? "bg-cyan-500/20 border-cyan-400" : "bg-[#0B1120] border-slate-700"
                    }`}>
                      {done ? <CheckCircle2 className="w-3.5 h-3.5 text-white" /> : active ? <Clock className="w-3.5 h-3.5 text-cyan-400" /> : null}
                    </div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold mt-2 text-center ${
                      done || active ? "text-slate-200" : "text-slate-600"
                    }`}>{step.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Job details */}
        <div className="grid sm:grid-cols-2 gap-3">
          <InfoCard icon={Car} label="Customer" value={job.customer_name} />
          <InfoCard icon={Shield} label="Insurer" value={job.insurance_company || "—"} sub={job.claim_number} />
        </div>

        {/* Evidence Bundle download (insurance-ready PDF) */}
        {calReport && (
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="track-evidence-bundle">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-blue-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-400">Calibration Evidence</div>
                <div className="font-display text-sm mt-0.5">Full Pre/Post-Scan + ALLDATA Bundle PDF</div>
                <div className="text-xs text-slate-500 mt-0.5">Calibration Report · scans · photos · OEM links — single PDF for your records</div>
              </div>
            </div>
            <a
              href={`${BACKEND_URL}/api/public/track/${jobToken}/bundle.pdf`}
              target="_blank" rel="noreferrer"
              data-testid="track-bundle-download"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        )}

        {/* Invoices with Pay Now */}
        {invoices.length > 0 && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="track-invoices">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400 mb-3">Invoices</div>
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-900/40 border border-slate-800/60 flex-wrap" data-testid={`track-invoice-${inv.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-blue-300">{inv.invoice_number}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{inv.type || "invoice"} · {inv.recipient_name || ""}</div>
                  </div>
                  <Badge className={`${statusColor[inv.status] || "bg-slate-700"} border uppercase text-[10px] tracking-wider`}>{inv.status}</Badge>
                  <span className="font-mono text-emerald-400 text-sm">${(inv.total || 0).toFixed(2)}</span>
                  {inv.status !== "paid" && inv.status !== "cancelled" && (
                    <Button
                      size="sm"
                      onClick={() => payInvoice(inv.id)}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid={`track-pay-${inv.id}`}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Pay
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ADAS detected */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-400 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> ADAS Analysis</div>
              <h3 className="font-display text-xl font-medium mt-1">Required Calibrations</h3>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total Cal Fees</div>
              <div className="font-mono text-xl text-cyan-400">${(adas.total_calibration_fees || 0).toFixed(2)}</div>
            </div>
          </div>
          {(adas.required_operations || []).length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">No ADAS operations detected yet.</div>
          ) : (
            <div className="space-y-2">
              {adas.required_operations.map((op, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800/60">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/30 text-[10px] uppercase tracking-wider">{op.type}</Badge>
                      <span className="text-sm font-medium">{op.name}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{op.system}</div>
                  </div>
                  <div className="font-mono text-emerald-400 text-sm">${op.fee.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
          {adas.summary && (
            <div className="mt-4 p-4 rounded-lg bg-cyan-500/5 border border-cyan-500/20 text-xs text-slate-300 leading-relaxed">
              <strong className="text-cyan-400">Analysis:</strong> {adas.summary}
            </div>
          )}
        </div>

        {/* Commission section removed — body_shop invoices appear in the Invoices section above with Pay button */}

        {/* Comments thread */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-cyan-400" />
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Messages & Timeline ({comments.length})</div>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto pr-2 mb-4">
            {comments.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">No messages yet. Use the form below to chat with TrueLine.</div>
            ) : comments.map((c) => (
              <div key={c.id} data-testid={`comment-${c.id}`} className={`p-3 rounded-lg border ${
                c.author_role === "system" ? "bg-slate-900/30 border-slate-800/40" :
                c.author_role === "admin" ? "bg-blue-500/5 border-blue-500/20" :
                "bg-cyan-500/5 border-cyan-500/20"
              }`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{c.author_name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className={`text-sm leading-relaxed ${c.author_role === "system" ? "italic text-slate-500" : "text-slate-200"}`}>{c.body}</div>
              </div>
            ))}
          </div>

          <div className="space-y-2 border-t border-slate-800/60 pt-4">
            <input
              data-testid="comment-author"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Your name (e.g. Mike at Premier)"
              className="w-full bg-[#030712] border border-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-cyan-500/50"
            />
            <div className="flex gap-2">
              <Textarea
                data-testid="comment-body"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ask TrueLine a question..."
                rows={2}
                className="flex-1 bg-[#030712] border-slate-800"
              />
              <Button data-testid="send-comment-btn" onClick={sendComment} disabled={sending || !newComment.trim()} className="bg-blue-600 hover:bg-blue-500 self-start">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-slate-600 pt-4">
          Powered by <span className="text-blue-400">TrueLine Calibration Platform</span> · Secure tracking link
        </div>
      </main>
    </div>
  );
}

const InfoCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</span>
      <Icon className="w-4 h-4 text-slate-600" />
    </div>
    <div className="font-display text-base font-medium">{value || "—"}</div>
    {sub && <div className="text-xs text-slate-500 font-mono mt-0.5">{sub}</div>}
  </div>
);
